import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import {either, option} from 'fp-ts';
import type {Either} from 'fp-ts/Either';
import type {Option} from 'fp-ts/Option';
import type ExampleGreeter from './immutable.js';
import type {Greeting, Planet} from './types.js';
import {GREETINGS, PLANETS, pickAtRandom} from './constants.js';

export enum FetchState {
  INITIAL,
  LOADING,
  SUCCESS,
  ERR,
}

export type Model = {
  greeting: Greeting;
  planet: Planet;
  messagesReceived: number;
  article: Option<Record<string, unknown>>;
  fetchState: FetchState;
  count: number;
};

export type Msg = VariantsFor<typeof Msg>;

export const Msg = makeVariants({
  setGreeting: Variant<Greeting>()('set-greeting'),
  setPlanet: Variant<Planet>()('set-planet'),
  randomize: Variant<void>()('randomize'),
  setArticle: Variant<Either<Error, Record<string, unknown>>>()('set-article'),
  setArticleFetchState: Variant<FetchState>()('set-artice-fetch-state'),
  setCount: Variant<number>()('set-count'),
  fetchArticle: Variant<void>()('fetch-article'),
  incrementMessagesReceived: Variant<void>()('increment-message-count'),
});

// -- Init

function init(props: InitialProperties): Readonly<Model> {
  const greeting = <Greeting>props.get('greeting') ?? GREETINGS[0];
  const planet = <Planet>props.get('planet') ?? PLANETS[0];

  return {
    greeting,
    planet,
    messagesReceived: 0,
    article: option.none,
    fetchState: FetchState.INITIAL,
    count: <number>props.get('count') ?? 0,
  } as const;
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-greeting':
      return {...model, greeting: msg.value} as const;

    case 'set-planet':
      return {...model, planet: msg.value} as const;

    case 'randomize':
      return {
        ...model,
        greeting: pickAtRandom([...GREETINGS]) as Greeting,
        planet: pickAtRandom([...PLANETS]) as Planet,
      } as const;

    case 'fetch-article': {
      return [
        {...model, fetchState: FetchState.LOADING},
        cmd((host: ExampleGreeter) => host.fetchIt(), Msg.setArticle),
      ] as const;
    }

    case 'set-article':
      return either.match<Error, Record<string, unknown>, Model>(
        () => ({
          ...model,
          article: option.none,
          fetchState: FetchState.ERR,
        }),
        article => ({
          ...model,
          article: option.some(article),
          fetchState: FetchState.SUCCESS,
        })
      )(msg.value);

    case 'set-count':
      return {...model, count: msg.value} as const;

    case 'set-artice-fetch-state':
      return {...model, fetchState: msg.value} as const;

    case 'increment-message-count':
      return {...model, messagesReceived: model.messagesReceived + 1} as const;
  }
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  extendsAdapter: {
    count: {
      resolver: model => model.count,
      variant: Msg.setCount,
    },
  },
};
